/**
 * Server-safe parser for the OneTrust `OptanonConsent` cookie.
 *
 * Mirrors the parsing behaviour of the browser-only `getConsentGroups`
 * / `hasPerformanceConsent` helpers in `./onetrust`, but accepts the
 * raw cookie value so it can run from Node — Next.js Server Components,
 * route handlers, `getServerSideProps`, middleware, etc.
 *
 * Cookie shape (URL-encoded segments separated by `&`):
 *   `...&groups=1:1,2:0,4:0&...`
 *
 * Group IDs:
 *   1 = Strictly Necessary
 *   2 = Performance / Analytics
 *   4 = Targeting
 *
 * When the cookie is missing or malformed, consent defaults to `false`
 * (deny) — the conservative choice for compliance. Unlike the browser
 * version this never consults Global Privacy Control (a navigator API);
 * callers that need GPC handling should layer it in.
 */

import type { ConsentGroups } from './onetrust';

const ALLOWED_GROUPS = new Set(['1', '2', '4']);

export function parseConsentGroupsFromCookie(optanonConsent: string | null | undefined): ConsentGroups {
	if (!optanonConsent) return {};

	const match = /groups=([^&;]*)/.exec(optanonConsent);
	const groupsValue = match ? match[1] : undefined;
	if (!groupsValue) return {};

	const groups: ConsentGroups = {};
	for (const entry of groupsValue.split(',')) {
		const [id, value] = entry.split(':');
		if (id && ALLOWED_GROUPS.has(id) && (value === '0' || value === '1')) {
			groups[id] = value === '1';
		}
	}
	return groups;
}

/**
 * Returns true when the **decoded value** of the `OptanonConsent` cookie grants
 * Performance / Analytics (group 2) consent. Deny-by-default.
 *
 * Pass the decoded value of the `OptanonConsent` cookie, **not** the full
 * `Cookie` request header. Extract and decode it first, for example:
 *   const raw = /(?:^|;\s*)OptanonConsent=([^;]*)/.exec(req.headers.cookie ?? '');
 *   const optanonValue = raw ? decodeURIComponent(raw[1]) : undefined;
 *   const hasConsent = hasPerformanceConsentFromCookie(optanonValue);
 */
export function hasPerformanceConsentFromCookie(optanonConsent: string | null | undefined): boolean {
	return parseConsentGroupsFromCookie(optanonConsent)['2'] === true;
}

/**
 * Returns true when the raw `OptanonConsent` cookie value grants
 * Targeting (group 4) consent. Deny-by-default.
 */
export function hasTargetingConsentFromCookie(optanonConsent: string | null | undefined): boolean {
	return parseConsentGroupsFromCookie(optanonConsent)['4'] === true;
}
